import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useMenu } from "../../context/MenuContext";
import { postUser } from "../../helper/http";
import { ToastService } from "../../service/toast.service";
import { useLanguage } from "../../context/LanguageContext";
import TwoColumnListInput, {
  TwoColumnBlock,
} from "../../components/TwoColumnListInput/TwoColumnListInput";
import styles from "./MenuEditorPage.module.css";

export default function MenuEditorPage() {
  const navigate = useNavigate();
  const { menuSettings, isLoading } = useMenu();
  const { translateLabel } = useLanguage();

  const [hiddenIds, setHiddenIds] = useState<string[]>([]);
  const [visibleIds, setVisibleIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const modules = menuSettings.modules || [];

    const hidden = modules
      .filter((m) => !m.favorite)
      .sort((a, b) => {
        const orderDiff = (a.order ?? 0) - (b.order ?? 0);
        if (orderDiff !== 0) return orderDiff;
        return a.id - b.id;
      })
      .map((m) => String(m.id));

    const visible = modules
      .filter((m) => !!m.favorite)
      .sort((a, b) => (a.favorite ?? 0) - (b.favorite ?? 0))
      .map((m) => String(m.id));

    setHiddenIds(hidden);
    setVisibleIds(visible);
  }, [menuSettings.modules]);

  const allModules = menuSettings.modules || [];

  const availableBlocks: TwoColumnBlock[] = allModules.map((m) => ({
    id: String(m.id),
    label: m.name,
    icon: "file",
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const modules = menuSettings.modules || [];
      const updates: Promise<any>[] = [];

      modules.forEach((m) => {
        const id = String(m.id);
        const visibleIndex = visibleIds.indexOf(id);
        const hiddenIndex = hiddenIds.indexOf(id);
        const isVisible = visibleIndex !== -1;
        const newFavorite = isVisible ? visibleIndex + 1 : null;
        const newOrder = isVisible ? visibleIndex + 1 : hiddenIndex + 1;

        updates.push(
          postUser("crud/upsert", {
            id: m.id,
            module: "module",
            body: {
              favorite: newFavorite,
              order: newOrder,
              table_name: m.table_name,
            },
          }),
        );
      });

      await Promise.all(updates);
      ToastService.success(translateLabel("saved") || "Saved");
    } catch (e) {
      console.error(e);
      ToastService.error(translateLabel("saveError") || "Save error");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <span>{translateLabel("loading") || "Loading..."}</span>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h1 className={styles.title}>
          {translateLabel("menuEditor") || "Menu Editor"}
        </h1>
        <button
          className={styles.saveButton}
          onClick={handleSave}
          disabled={saving}
        >
          <FontAwesomeIcon icon={faSave} />
          <span>
            {saving
              ? translateLabel("saving") || "Saving..."
              : translateLabel("save") || "Save"}
          </span>
        </button>
      </div>

      <div className={styles.content}>
        <TwoColumnListInput
          leftValue={hiddenIds}
          rightValue={visibleIds}
          onLeftChange={setHiddenIds}
          onRightChange={setVisibleIds}
          availableBlocks={availableBlocks}
          leftLabel={translateLabel("hidden") || "Not shown"}
          rightLabel={translateLabel("visible") || "Shown in menu"}
        />
      </div>
    </div>
  );
}
